import i18n from '@/shared/i18n';
import { parseResourceName } from './api-utils';

/**
 * Converts a File object to data URI string (data:[<mimetype>][;base64],<data>)
 * @param file - The File object to convert
 * @returns Promise that resolves to base64 URI
 */
export const fileToDataUri = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Backend prefixes the file id to the filename, eg: "1234567890--filename.pdf"
 * We need to remove the prefix to get the actual filename.
 * Only the first part before the first "--" is removed.
 *
 * @param fileName - The filename with optional id prefix
 * @returns The display name of the file, eg: "filename.pdf"
 *
 * @example
 * getFileDisplayName('1234567890--filename.pdf') // returns 'filename.pdf'
 * getFileDisplayName('abc--The-number---Google-Docs.pdf') // returns 'The-number---Google-Docs.pdf'
 * getFileDisplayName('no-prefix.pdf') // returns 'no-prefix.pdf'
 */
export const getFileDisplayName = (fileName: string): string => {
  if (!fileName) return '';
  const separatorIndex = fileName.indexOf('--');
  if (separatorIndex === -1) {
    return fileName;
  }
  return fileName.slice(separatorIndex + 2);
};

/**
 * Extracts a filename's extension, including the leading dot, lowercased.
 *
 * @param fileName - The filename to read the extension from
 * @returns The extension with its dot, or '' when there is none
 *
 * @example
 * getFileExtension('Q1 Report.PDF') // returns '.pdf'
 * getFileExtension('.env')          // returns '' (leading dot is the stem)
 * getFileExtension('README')        // returns ''
 */
export const getFileExtension = (fileName?: string): string => {
  const dotIndex = fileName?.lastIndexOf('.') ?? -1;
  return dotIndex > 0 ? fileName!.slice(dotIndex).toLowerCase() : '';
};

/**
 * Converts a Blob to base64 string (without data URI prefix)
 * @param blob - The Blob object to convert
 * @returns Promise that resolves to base64 content string
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix to get just the base64 content
      const base64Content = base64String.split(',')[1];
      resolve(base64Content);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * A file that may carry its bytes inline.
 *
 * Deliberately structural: `src/shared` must not depend on a feature module,
 * so anything with a discriminator and a base64 payload fits, including the
 * outputs module's resolved-file union.
 */
export interface IInlineFile {
  /** Discriminator; only `'file-inline'` carries the bytes with the value. */
  type: string;
  /** Base64 payload, present on inline files. */
  content?: unknown;
}

/**
 * Byte size of a file when it can be known without a fetch, measured from the
 * base64 payload an inline file already carries.
 *
 * @param file - The file to measure
 * @returns The decoded byte count, or null when the bytes are not inline
 *
 * @example
 * getInlineFileSizeBytes({ type: 'file-inline', content: btoa('hello world') }) // 11
 * getInlineFileSizeBytes({ type: 'file-remote' }) // null
 */
export const getInlineFileSizeBytes = (file: IInlineFile): number | null => {
  if (file.type !== 'file-inline') return null;
  const content = file.content;
  if (typeof content !== 'string' || content.length === 0) return null;
  const padding = content.endsWith('==') ? 2 : content.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((content.length * 3) / 4) - padding);
};

/**
 * Extracts a human-readable filename from a `Content-Disposition` response header.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''…` parameter (percent-encoded UTF-8) over
 * the plain `filename=` parameter. Falls back to `fallback` when neither is present.
 *
 * @param contentDisposition - Value of the `Content-Disposition` response header
 * @param fallback - Returned as-is when no filename can be parsed
 * @returns Decoded, human-readable filename
 *
 * @example
 * // Server: attachment; filename="<base64Id>"; filename*=UTF-8''%E0%B8%9A%E0%B8%A3%E0%B8%B4.docx
 * parseContentDispositionFilename(header, 'fallback'); // → 'บริ.docx'
 */
export const parseContentDispositionFilename = (
  contentDisposition: string | undefined,
  fallback: string,
): string => {
  if (contentDisposition) {
    // RFC 5987 filename* — UTF-8 percent-encoded, takes precedence.
    // The charset token is followed by an optional language tag and a value:
    //   filename*=UTF-8'en'encoded  or  filename*=UTF-8''encoded
    // Use [^']* for the language tag so any (or no) language is accepted.
    const starMatch = contentDisposition.match(
      /filename\*\s*=\s*UTF-8'[^']*'([^;\s]+)/i,
    );
    if (starMatch?.[1]) {
      try {
        return decodeURIComponent(starMatch[1]);
      } catch {
        // malformed percent-encoding — fall through
      }
    }

    // Plain filename= fallback — quoted or unquoted.
    // Bishop may percent-encode the value here too (e.g. when the storage ID
    // itself was derived from a percent-encoded path segment), so we attempt
    // decoding and return the raw string if it fails.
    const plainMatch = contentDisposition.match(
      /filename\s*=\s*(?:"([^"]+)"|([^";\s][^;]*))/,
    );
    const raw = (plainMatch?.[1] ?? plainMatch?.[2])?.trim();
    if (raw) {
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }

  return fallback;
};

/**
 * Downloads a blob as a file by creating a temporary anchor element.
 * Triggers a browser download with the specified filename.
 *
 * @param blob - The Blob object to download
 * @param filename - The name to save the file as
 *
 * @example
 * ```typescript
 * const blob = new Blob(['content'], { type: 'text/plain' });
 * downloadBlob(blob, 'document.txt');
 * ```
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Triggers a browser download from a (typically presigned) remote URL by
 * creating a transient anchor element and clicking it. Unlike
 * {@link downloadBlob}, the bytes are not fetched in-app; the browser handles
 * the download directly from the URL.
 *
 * Used for server-generated download URLs (e.g. bishop's batch-download archive
 * URIs / single-asset download URIs, and automation export ZIPs). These point at
 * cross-origin storage where the `download` attribute is ignored by the browser,
 * but the presigned URL is served with `Content-Disposition: attachment`, so the
 * browser saves rather than navigates.
 *
 * @param url - The (presigned) remote URL to download from
 * @param filename - Optional suggested filename (honored only for same-origin URLs)
 *
 * @example
 * downloadUrl(response.archive_uri); // saves the server-built ZIP
 */
export const downloadUrl = (url: string, filename?: string): void => {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Checks if a source string looks like a file resource name that can be downloaded.
 * A file resource name either:
 * - Contains a resource path with "files/" (e.g., "organizations/abc/files/xyz--doc.pdf")
 * - Ends with a file extension (e.g., "xyz--invoice.pdf", "report.csv")
 *
 * Descriptive source names like "SAP Goods Receipt" or "sow_document" are NOT file resource names
 * since they don't have a file extension.
 *
 * @param source - The source string to check
 * @returns True if the source looks like a downloadable file resource name
 *
 * @example
 * isFileResourceName('organizations/abc/files/xyz--doc.pdf') // true
 * isFileResourceName('xyz--invoice.pdf') // true
 * isFileResourceName('report.csv') // true
 * isFileResourceName('sow_document') // false
 * isFileResourceName('SAP Goods Receipt') // false
 * isFileResourceName('') // false
 */
export const isFileResourceName = (source: string): boolean => {
  if (!source) return false;
  // Full resource path containing "files/" is always a file
  if (source.includes('files/')) return true;
  // Check if the last segment (after any /) has a file extension
  const lastSegment = source.split('/').pop() || '';
  return /\.\w{1,10}$/.test(lastSegment);
};

/**
 * Extracts the file ID from a source string that may be a full resource path or just the file ID.
 * Astral may send source as:
 * - Full path: "organizations/qJYteaPjsdsqcKulYTqe8/files/PpULROMi3EPMlYIGNVXDG--Boarding-pass3.pdf"
 * - Just file ID: "PpULROMi3EPMlYIGNVXDG--Boarding-pass3.pdf"
 *
 * @param source - The source string to extract file ID from
 * @returns The file ID (last part after "files/" or the original string if no path)
 *
 * @example
 * extractFileIdFromSource('organizations/abc/files/xyz--doc.pdf') // returns 'xyz--doc.pdf'
 * extractFileIdFromSource('xyz--doc.pdf') // returns 'xyz--doc.pdf'
 * extractFileIdFromSource('') // returns ''
 */
export const extractFileIdFromSource = (source: string): string => {
  if (!source) return '';

  // Check if it's a full resource path with "files/" in it
  const filesIndex = source.lastIndexOf('files/');
  if (filesIndex !== -1) {
    return source.slice(filesIndex + 6); // 6 is length of "files/"
  }

  // Otherwise, return as-is (already just the file ID)
  return source;
};

/**
 * Gets the display name for a source, handling both full resource paths and file IDs.
 * Combines extractFileIdFromSource and getFileDisplayName for convenience.
 *
 * @param source - The source string (can be full resource path or file ID with prefix)
 * @returns The human-readable display name
 *
 * @example
 * getSourceDisplayName('organizations/abc/files/xyz--invoice.pdf') // returns 'invoice.pdf'
 * getSourceDisplayName('xyz--invoice.pdf') // returns 'invoice.pdf'
 * getSourceDisplayName('SAP System') // returns 'SAP System'
 * getSourceDisplayName('') // returns ''
 */
export const getSourceDisplayName = (source: string): string => {
  if (!source) return '';

  // First extract file ID from full resource path (if applicable)
  const fileId = extractFileIdFromSource(source);

  // Then remove the ID prefix to get display name
  return getFileDisplayName(fileId);
};

/**
 * Recovers the original human filename from a Bishop file resource name or id.
 *
 * Bishop's canonical file id is `{nanoid}--{base64url(stem)}.{ext}` (see
 * bishop `file_id.rs`), so stripping the `{nanoid}--` prefix alone (what
 * `getSourceDisplayName` does) leaves the base64url-encoded stem. This decodes
 * that stem and re-appends the literal extension. A slug that isn't a canonical
 * base64url stem (a legacy literal name, or content that isn't valid UTF-8 once
 * decoded) is returned unchanged, so this is safe on non-canonical inputs.
 *
 * @param source - Full resource path, bare file id, or slug
 * @param options Require a canonical bare file ID and return undefined when metadata is needed.
 * @returns The decoded filename; legacy calls retain the original slug on failure.
 *
 * @example
 * decodeBishopFilename('organizations/o/workspaces/w/files/abc--cHJvYmU.pdf') // 'probe.pdf'
 * decodeBishopFilename('report.csv') // 'report.csv'
 */
export function decodeBishopFilename(source: string): string;
export function decodeBishopFilename(
  source: string,
  options: { requireCanonicalId: true },
): string | undefined;
export function decodeBishopFilename(
  source: string,
  options?: { requireCanonicalId: true },
): string | undefined {
  const canonical = options?.requireCanonicalId;
  const match = canonical
    ? source.match(
        /^[A-Za-z0-9_-]{21}--([A-Za-z0-9_-]+(?:\.[A-Za-z0-9]{1,16})?)$/,
      )
    : undefined;
  if (canonical && !match) return undefined;
  const slug = match?.[1] ?? getSourceDisplayName(source);
  const fallback = canonical ? undefined : slug;
  if (!slug) return slug;

  // Canonical slug = base64url(stem) then a literal `.ext`; base64url never
  // contains '.', so the last dot unambiguously starts the extension.
  const dot = slug.lastIndexOf('.');
  const b64 = dot >= 0 ? slug.slice(0, dot) : slug;
  const ext = dot >= 0 ? slug.slice(dot) : '';
  if (!b64 || !/^[A-Za-z0-9\-_]+$/.test(b64)) return fallback;

  try {
    const standard = b64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4);
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    // Bishop IDs can truncate stems at a UTF-8 boundary below 150 bytes.
    if (
      canonical &&
      (bytes.length >= 147 ||
        btoa(binary)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '') !== b64)
    )
      return undefined;
    // `fatal` rejects a non-canonical slug that isn't valid UTF-8 once decoded,
    // so we fall back to the raw slug rather than emitting mojibake.
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes) + ext;
  } catch {
    return fallback;
  }
}

/**
 * Bishop stems often keep the separator that preceded the extension, so a file
 * arrives as `notes..txt`. Show the one dot a reader expects.
 */
const collapseTrailingStemDots = (name: string): string =>
  name.replace(/\.+(?=\.[^.]+$)/, '');

/**
 * Decodes Bishop's base64 file-name stem while preserving its extension.
 *
 * Deliberately stricter than `decodeBishopFilename`: surfaces that show names
 * Bishop never encoded need a short literal name left alone, and a short one is
 * valid base64url by accident — that util reads `Q1.pdf` as `C.pdf` and
 * `README` as `D@\x030`. Demanding an extension, at least eight stem characters
 * and a printable decode keeps a literal name literal.
 *
 * @param name - A bare file name, already stripped of any resource path
 * @returns The decoded name, or the original when it isn't a canonical stem
 *
 * @example
 * getReadableFileName('c2FtcGxlX25vdGVzLg.txt') // 'sample_notes.txt'
 * getReadableFileName('invoice.pdf') // 'invoice.pdf'
 * getReadableFileName('notes..txt') // 'notes.txt'
 */
export const getReadableFileName = (name: string): string => {
  const match = name.match(/^([A-Za-z0-9_-]{8,})\.([A-Za-z0-9]+)$/);
  if (!match) return collapseTrailingStemDots(name);

  try {
    const normalized = match[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalized).replace(/\.$/, '');
    return /^[\x20-\x7E]+$/.test(decoded) ? `${decoded}.${match[2]}` : name;
  } catch {
    return collapseTrailingStemDots(name);
  }
};

/**
 * The name to show for a file source, whether it arrived as a resource path, a
 * bare file id or a plain name. Reduces the source to its file id, strips
 * Bishop's `{nanoid}--` prefix, then decodes the base64 stem on top.
 *
 * @param source - Full resource path, bare file id, URL or literal file name
 * @returns The human-readable file name
 *
 * @example
 * getReadableSourceName('organizations/acme/files/abc--c2FtcGxlX25vdGVzLg.txt') // 'sample_notes.txt'
 * getReadableSourceName('statement.xlsx') // 'statement.xlsx'
 */
export const getReadableSourceName = (source: string): string =>
  getReadableFileName(getSourceDisplayName(source));

/**
 * Parsed components of a Bishop file resource name.
 */
export interface IParsedFileResource {
  /** Organization ID */
  organization: string;
  /** File ID (may include the storage-ID prefix, e.g. "abc--name.pdf") */
  file: string;
  /** Workspace ID — present only for workspace-scoped resources */
  workspace?: string;
}

/**
 * Parses a Bishop file resource name into its component IDs.
 *
 * Supported formats:
 *   organizations/{org}/files/{file}
 *   organizations/{org}/workspaces/{workspace}/files/{file}
 *
 * Accepts an optional leading slash which is normalised away before parsing.
 *
 * @param resourceName - Full resource path to parse
 * @returns Parsed IDs, or null when the format is not a valid file resource
 *
 * @example
 * parseFileResourceName('organizations/acme/files/abc--doc.pdf')
 * // { organization: 'acme', file: 'abc--doc.pdf' }
 *
 * parseFileResourceName('organizations/acme/workspaces/ws1/files/abc--doc.pdf')
 * // { organization: 'acme', workspace: 'ws1', file: 'abc--doc.pdf' }
 */
export const parseFileResourceName = (
  resourceName: string,
): IParsedFileResource | null => {
  if (!resourceName) return null;

  const normalised = resourceName.trim().replace(/^\/+/, '');
  const parts = parseResourceName(normalised);

  if (
    !parts.organization ||
    !parts.file ||
    !normalised.startsWith('organizations/') ||
    !normalised.includes('/files/')
  ) {
    return null;
  }

  return {
    organization: parts.organization,
    file: parts.file,
    ...(parts.workspace ? { workspace: parts.workspace } : {}),
  };
};

/**
 * Formats a MIME type string into a user-friendly display name.
 *
 * Handles patterns like:
 * - application/pdf -> pdf
 * - text/html -> html
 * - image/png -> png
 * - application/vnd.openxmlformats-officedocument.spreadsheetml.sheet -> xlsx
 * - application/x-yaml -> yaml
 *
 * @param mimeType - The MIME type string (e.g., "application/pdf", "text/html")
 * @returns A user-friendly display name (e.g., "pdf", "html", "docx")
 *
 * @example
 * formatMimeType('application/pdf') // returns 'pdf'
 * formatMimeType('text/html') // returns 'html'
 * formatMimeType('application/json') // returns 'json'
 * formatMimeType('application/vnd.openxmlformats-officedocument.wordprocessingml.document') // returns 'docx'
 */
export const formatMimeType = (mimeType: string): string => {
  if (!mimeType) {
    return '';
  }

  const lowerMimeType = mimeType.toLowerCase();

  // Handle long Microsoft Office MIME types
  if (
    lowerMimeType ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return i18n.t('common.docx', { ns: 'common' });
  } else if (
    lowerMimeType ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return i18n.t('common.xlsx', { ns: 'common' });
  } else if (
    lowerMimeType ===
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return i18n.t('common.pptx', { ns: 'common' });
  }

  // unknown MIME types
  if (lowerMimeType === 'unknown/type') {
    return i18n.t('common.unknown', { ns: 'common' });
  }

  // Regex to extract the subtype after the slash, handling:
  // - type/subtype (e.g., application/pdf, text/html, image/png)
  // - type/x-subtype (e.g., application/x-yaml) - for deprecated aliases
  // - type/vnd.subtype (e.g., application/vnd.ms-excel)
  // - type/subtype+suffix (e.g., image/svg+xml)
  const match = mimeType.match(/^[^/]+\/(?:x-|vnd\.)?([^+]+)/i);

  const fileType = match?.[1] ?? mimeType;

  return i18n.t('common.fileType', {
    ns: 'common',
    fileType: fileType,
  });
};

/** MIME types browsers commonly report for ZIP archives. */
const ZIP_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/zip-compressed',
  'multipart/x-zip',
]);

/**
 * Returns whether a file is a ZIP archive.
 *
 * Checks both the reported MIME type and the `.zip` extension, since some
 * browsers/OSes report an empty or generic type for ZIP files.
 *
 * @param file - The file to check
 * @returns True if the file looks like a ZIP archive
 */
export const isZipFile = (file: File): boolean => {
  return (
    ZIP_MIME_TYPES.has(file.type.toLowerCase()) ||
    file.name.toLowerCase().endsWith('.zip')
  );
};
